// Single-line legend explaining what each automatic highlight color
// means. Used under the color-filter chip strips on Customers and
// Inactive Sites pages so Dave doesn't have to remember which colour
// means "price increase sent" vs "reactivation sent".
//
// Only renders entries for colors that actually have count > 0, so a
// fresh install (no flags stamped yet) shows no legend clutter.
// Manual highlight colors (amber, red, yellow, blue, purple, grey) are
// summarised in a single trailing "Other colors = manual highlight"
// line rather than enumerated — Dave sets those by hand on individual
// customer records, no shared semantic meaning.

const COLOR_MEANINGS = {
  // Outreach cadence (Phase B, Dave 2026-06-09). Displayed FIRST in
  // the legend so the most actionable signals lead.
  cadenceYellow: { hex: '#a16207', label: 'Initial outreach sent' },
  cadenceOrange: { hex: '#c2410c', label: 'Follow-up sent' },
  cadenceRed:    { hex: '#b91c1c', label: 'Breakup ("close the file?") sent' },
  orange: { hex: '#ED7D31', label: 'Price Increase email sent' },
  green:  { hex: '#047857', label: 'Reactivation (win-back) email sent' }
};

export default function ColorLegend({ colorCounts = {} }) {
  // Auto-colors actually present in the data.
  const automatic = Object.entries(COLOR_MEANINGS)
    .filter(([name]) => colorCounts[name]);

  // Any color present that isn't one of the two automatic ones is a
  // manual highlight. Decide whether to show the "Other colors" caption.
  const hasManual = Object.keys(colorCounts).some(
    (name) => !COLOR_MEANINGS[name]
  );

  if (automatic.length === 0 && !hasManual) return null;

  return (
    <div style={{
      fontSize: 'var(--text-small)',
      color: 'var(--color-text-dim)',
      // Negative top margin pulls the legend tight under the chip strip
      // so it reads as part of the same control, not a separate row.
      marginTop: 'calc(-1 * var(--space-2))',
      marginBottom: 'var(--space-3)',
      display: 'flex',
      gap: 'var(--space-4)',
      flexWrap: 'wrap',
      alignItems: 'center'
    }}>
      <span style={{ fontWeight: 'var(--font-semibold)' }}>Legend:</span>
      {automatic.map(([name, { hex, label }]) => (
        <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{
            display: 'inline-block',
            width: 10, height: 10,
            borderRadius: '50%',
            background: hex,
            flex: '0 0 auto'
          }} />
          {/* Cadence entries skip the redundant "Yellow = ..." prefix
              and just render the label in the chip color. Historical
              colors (orange / green) keep the legacy two-part format. */}
          {name.startsWith('cadence') ? (
            <span style={{ color: hex, fontWeight: 'var(--font-semibold)' }}>{label}</span>
          ) : (
            <>
              <span style={{
                color: hex,
                fontWeight: 'var(--font-semibold)',
                textTransform: 'capitalize'
              }}>{name}</span>
              <span>= {label}</span>
            </>
          )}
        </span>
      ))}
      {hasManual && (
        <span style={{ fontStyle: 'italic' }}>
          Other colors = manual highlight
        </span>
      )}
    </div>
  );
}
